import {
  axiosBaseQuery,
  BaseQueryError,
  createAxiosInstance,
  errorHandlingInterceptor
} from '@client';
import { BankAccountResponse } from '@module/bank-account/model/BankAccountResponse';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import VerbalVerifyBankAccountDTO from '@state/types/VerbalVerifyBankAccountDTO';

const updateFetchAgentBankAccountCache = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  data: BankAccountResponse
) => {
  dispatch(
    agentBankAccountsApi.util.upsertQueryData(
      'fetchAgentBankAccount',
      { bankAccountId: data.id },
      data
    )
  );
};

export const agentBankAccountsApi = createApi({
  baseQuery: axiosBaseQuery({
    customAxiosInstance: createAxiosInstance({
      errorInterceptor: errorHandlingInterceptor({
        handleUnauthorizedResponse: false
      })
    })
  }),
  tagTypes: ['AgentBankAccounts', 'BANK_ACCOUNT'],
  reducerPath: 'agent-bank-accounts_api',
  keepUnusedDataFor: 0,
  endpoints: (build) => ({
    fetchAgentBankAccounts: build.query<
      BankAccountResponse[],
      { currency?: string; buyerId?: string }
    >({
      query: ({ currency, buyerId }) => ({
        url: `/secured/supplier-bank-accounts`,
        method: 'GET',
        params: {
          currency,
          buyerId
        }
      }),
      providesTags: (res, error, { currency }) =>
        res ? [{ type: 'AgentBankAccounts', id: currency }] : [],
      transformErrorResponse: (error) => {
        if ((error as BaseQueryError)?.status === 403) {
          document.location.href = '/error-no-access';
        }
      }
    }),
    fetchAgentBankAccount: build.query<
      BankAccountResponse,
      { bankAccountId: number }
    >({
      query: ({ bankAccountId }) => ({
        url: `/secured/bank-account/${bankAccountId}/load`,
        method: 'GET'
      }),
      transformErrorResponse: (error) => {
        if ((error as BaseQueryError)?.status === 403) {
          document.location.href = '/error-no-access';
        }
      },
      providesTags: (res, _err, { bankAccountId }) =>
        res ? [{ type: 'BANK_ACCOUNT', id: bankAccountId }] : ['BANK_ACCOUNT']
    }),
    updateAgentBankAccount: build.mutation<BankAccountResponse, FormData>({
      query: (data) => ({
        url: '/secured/bank-account/save',
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        data
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          updateFetchAgentBankAccountCache(dispatch, data);
        } catch {
          //
        }
      },
      transformErrorResponse: (error) => {
        if ((error as BaseQueryError)?.status === 403) {
          document.location.href = '/error-no-access';
        } else {
          return error;
        }
      }
    }),
    approveAgentBankAccount: build.mutation<BankAccountResponse, FormData>({
      query: (data) => ({
        url: '/secured/bank-account/approve',
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        data
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          updateFetchAgentBankAccountCache(dispatch, data);
        } catch {
          //
        }
      },
      transformErrorResponse: (error) => {
        if ((error as BaseQueryError)?.status === 403) {
          document.location.href = '/error-no-access';
        }
      }
    }),
    updateApprovedAgentBankAccount: build.mutation<
      BankAccountResponse,
      FormData
    >({
      query: (data) => ({
        url: '/secured/bank-account/edit-approved',
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        data
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          updateFetchAgentBankAccountCache(dispatch, data);
        } catch {
          //
        }
      },
      transformErrorResponse: (error) => {
        if ((error as BaseQueryError)?.status === 403) {
          document.location.href = '/error-no-access';
        }
      }
    }),
    verbalVerifyAgentBankAccount: build.mutation<
      BankAccountResponse,
      VerbalVerifyBankAccountDTO
    >({
      query: (data) => ({
        url: '/secured/bank-account/verbal-verify',
        method: 'PATCH',
        data
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          updateFetchAgentBankAccountCache(dispatch, data);
        } catch {
          //
        }
      }
    })
  })
});

export type StateWithAgentBankAccountsApi = {
  [agentBankAccountsApi.reducerPath]: ReturnType<
    typeof agentBankAccountsApi.reducer
  >;
};

export const {
  useLazyFetchAgentBankAccountsQuery,
  useFetchAgentBankAccountsQuery,
  useFetchAgentBankAccountQuery,
  useUpdateAgentBankAccountMutation,
  useApproveAgentBankAccountMutation,
  useUpdateApprovedAgentBankAccountMutation,
  useVerbalVerifyAgentBankAccountMutation
} = agentBankAccountsApi;

export const refreshBankAccountsAction = (currency: string) =>
  agentBankAccountsApi.util.invalidateTags([
    { type: 'AgentBankAccounts', id: currency }
  ]);
